import React, { useState } from 'react';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import TableChartIcon from '@material-ui/icons/TableChart';
import WhatsAppIcon from '@material-ui/icons/Chat';
import PrintIcon from '@material-ui/icons/Print';
import { selectCurrentRoute } from '../../store/slices/routesSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { selectDrivers } from '../../store/slices/driversSlice';
import { selectUI } from '../../store/slices/uiSlice';
import { t } from '../../i18n';
import SmsIcon from '@material-ui/icons/Sms';
import { exportPDF, exportExcel, buildWhatsAppMessage, buildSMSMessage } from '../../utils/exportUtils';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  btn: {
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '8px 16px',
    borderRadius: 8,
    textTransform: 'none',
    flex: '1 1 auto',
    minWidth: 120,
  },
  pdfBtn: {
    background: 'linear-gradient(135deg, #ff5252 0%, #cc0000 100%)',
    color: '#fff',
    '&:hover': { background: 'linear-gradient(135deg, #ff7070 0%, #ee2222 100%)' },
  },
  xlsBtn: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#ffffff',
    '&:hover': { background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)' },
  },
  waBtn: {
    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
    color: '#fff',
    '&:hover': { background: 'linear-gradient(135deg, #44df7a 0%, #25a99a 100%)' },
  },
  smsBtn: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    color: '#fff',
    '&:hover': { background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)' },
  },
  printBtn: {
    border: '1px solid rgba(0,0,0,0.12)',
    color: 'var(--surf-muted, #64748b)',
    '&:hover': { borderColor: 'rgba(59,130,246,0.4)', color: '#3b82f6' },
  },
}));

function ExportBar() {
  const classes = useStyles();
  const route = useSelector(selectCurrentRoute);
  const vehicles = useSelector(selectVehicles);
  const drivers = useSelector(selectDrivers);
  const { lang } = useSelector(selectUI);
  const [loading, setLoading] = useState(null);

  const vehicle = vehicles.find(v => v.id === route.vehicleId);
  const driver = drivers.find(d => d.id === route.driverId);

  async function handlePDF() {
    setLoading('pdf');
    try {
      await exportPDF(route, vehicle, driver);
    } catch (e) {
      console.error('PDF export error:', e);
    }
    setLoading(null);
  }

  async function handleExcel() {
    setLoading('excel');
    try {
      await exportExcel(route, vehicle, driver);
    } catch (e) {
      console.error('Excel export error:', e);
    }
    setLoading(null);
  }

  function handleWhatsApp() {
    const msg = buildWhatsAppMessage(route, vehicle, driver);
    const driverPhone = driver?.phone?.replace(/\s+/g, '').replace('+', '');
    const url = driverPhone
      ? `https://wa.me/${driverPhone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handleSMS() {
    const msg = buildSMSMessage(route);
    const driverPhone = driver?.phone?.replace(/\s+/g, '') || '';
    const url = `sms:${driverPhone}?body=${encodeURIComponent(msg)}`;
    window.open(url);
  }

  function handlePrint() {
    window.print();
  }

  return (
    <Box className={classes.root}>
      <Button
        variant="contained"
        className={`${classes.btn} ${classes.pdfBtn}`}
        startIcon={loading === 'pdf' ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfIcon />}
        onClick={handlePDF}
        disabled={!!loading || route.status !== 'ready'}
      >
        {t(lang, 'exportPDF')}
      </Button>

      <Button
        variant="contained"
        className={`${classes.btn} ${classes.xlsBtn}`}
        startIcon={loading === 'excel' ? <CircularProgress size={14} color="inherit" /> : <TableChartIcon />}
        onClick={handleExcel}
        disabled={!!loading || route.status !== 'ready'}
      >
        {t(lang, 'exportExcel')}
      </Button>

      <Button
        variant="contained"
        className={`${classes.btn} ${classes.waBtn}`}
        startIcon={<WhatsAppIcon />}
        onClick={handleWhatsApp}
        disabled={route.status !== 'ready'}
      >
        {t(lang, 'sendWhatsApp')}
      </Button>

      <Button
        variant="contained"
        className={`${classes.btn} ${classes.smsBtn}`}
        startIcon={<SmsIcon />}
        onClick={handleSMS}
        disabled={route.status !== 'ready'}
      >
        SMS
      </Button>

      <Button
        variant="outlined"
        className={`${classes.btn} ${classes.printBtn}`}
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        disabled={route.status !== 'ready'}
      >
        {t(lang, 'print')}
      </Button>
    </Box>
  );
}

export default ExportBar;
